import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Batch } from '@/types';
import { batchApi } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface BatchContextType {
  currentBatch: Batch | null;
  batches: Batch[];
  setCurrentBatch: (batch: Batch) => Promise<void>;
  loading: boolean;
  error: string | null;
  refetchBatches: () => Promise<void>;
  hasBatches: boolean;
}

const BatchContext = createContext<BatchContextType | undefined>(undefined);

export function BatchProvider({ children }: { children: ReactNode }) {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [currentBatch, setCurrentBatch] = useState<Batch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Get localStorage key for current batch preference (user + tenant specific)
  const getCurrentBatchKey = () => {
    const tenantCode = localStorage.getItem('tenantCode');
    const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('userName') || 'default';
    return `currentBatch_${tenantCode}_${userEmail}`;
  };

  // Get user's current batch preference from localStorage
  const getUserCurrentBatch = (availableBatches: Batch[]): Batch | null => {
    const currentBatchKey = getCurrentBatchKey();
    const savedBatchId = localStorage.getItem(currentBatchKey);

    if (savedBatchId) {
      const batch = availableBatches.find(b => b.id === savedBatchId);
      if (batch) {
        console.log('BatchContext: Found user current batch from localStorage:', batch);
        return batch;
      } else {
        // Clean up invalid preference
        localStorage.removeItem(currentBatchKey);
        console.log('BatchContext: Removed invalid batch preference from localStorage');
      }
    }

    return null;
  };

  // Save user's current batch preference to localStorage
  const saveUserCurrentBatch = (batch: Batch) => {
    const currentBatchKey = getCurrentBatchKey();
    localStorage.setItem(currentBatchKey, batch.id);
    console.log('BatchContext: Saved user current batch to localStorage:', batch.year);
  };

  const fetchBatches = async () => {
    // Skip fetching if not authenticated
    if (!isAuthenticated) {
      console.log('BatchContext: Not authenticated, skipping batches fetch');
      setLoading(false);

      // Use mock data when not authenticated
      console.log('BatchContext: Using mock data');
      const mockBatches = [
        { id: '1', year: '2023-2024', startDate: '2023-08-01', endDate: '2024-05-31', isCurrent: true },
        { id: '2', year: '2024-2025', startDate: '2024-08-01', endDate: '2025-05-31', isCurrent: false }
      ];
      setBatches(mockBatches);
      setCurrentBatch(mockBatches[0]);
      setError(null);
      return;
    }

    // Set tenant header from localStorage
    const tenantCode = localStorage.getItem('tenantCode');
    if (tenantCode) {
      axios.defaults.headers.common['X-Tenant-ID'] = tenantCode;
      console.log('BatchContext: Set X-Tenant-ID header:', tenantCode);
    }

    try {
      setLoading(true);
      console.log('BatchContext: Fetching batches...');

      // Add a small delay to ensure authentication is fully processed
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        const batchData = await batchApi.getAll();
        console.log('BatchContext: Batches fetched:', batchData);

        // Only update state if we have data
        if (Array.isArray(batchData)) {
          // Filter out archived batches by default
          const activeBatches = batchData.filter((batch: any) => !batch.isArchived);
          setBatches(activeBatches);

          // Use user-specific current batch logic
          if (activeBatches.length === 1) {
            // If there's only one batch, automatically set it as user's current batch
            console.log('BatchContext: Only one batch found, automatically setting as current:', activeBatches[0]);
            setCurrentBatch(activeBatches[0]);
            saveUserCurrentBatch(activeBatches[0]);
          } else if (activeBatches.length > 1) {
            // Multiple batches - check user's preference from localStorage
            console.log('BatchContext: Multiple batches found, checking user preference');

            const userCurrentBatch = getUserCurrentBatch(activeBatches);
            if (userCurrentBatch) {
              console.log('BatchContext: Using user preferred batch:', userCurrentBatch);
              setCurrentBatch(userCurrentBatch);
            } else {
              console.log('BatchContext: No user preference found, will show batch selection modal');
              setCurrentBatch(null);
            }
          } else {
            console.log('BatchContext: No batches available');
            setCurrentBatch(null);
          }

          setError(null);
        } else {
          console.error('BatchContext: Invalid response format for batches');

          // Fall back to mock data
          console.log('BatchContext: Falling back to mock data');
          const mockBatches = [
            { id: '1', year: '2023-2024', startDate: '2023-08-01', endDate: '2024-05-31' },
            { id: '2', year: '2024-2025', startDate: '2024-08-01', endDate: '2025-05-31' }
          ];
          setBatches(mockBatches);
          setCurrentBatch(mockBatches[0]);
          setError(null);
        }
      } catch (apiError) {
        console.error('BatchContext: API error fetching batches:', apiError);

        // Check if it's an authentication error (401)
        if (axios.isAxiosError(apiError) && apiError.response?.status === 401) {
          console.error('BatchContext: Authentication error when fetching batches');
          // Let the API interceptor handle the redirect

          // Fall back to mock data
          console.log('BatchContext: Falling back to mock data due to 401');
          const mockBatches = [
            { id: '1', year: '2023-2024', startDate: '2023-08-01', endDate: '2024-05-31' },
            { id: '2', year: '2024-2025', startDate: '2024-08-01', endDate: '2025-05-31' }
          ];
          setBatches(mockBatches);
          setCurrentBatch(mockBatches[0]);
          setError(null);
          return;
        }

        // For other errors, show a toast
        let errorMessage = 'Failed to load batches';
        if (apiError instanceof Error) {
          errorMessage += `: ${apiError.message}`;
        }

        setError(errorMessage);
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });

        // Fall back to mock data
        console.log('BatchContext: Falling back to mock data due to error');
        const mockBatches = [
          { id: '1', year: '2023-2024', startDate: '2023-08-01', endDate: '2024-05-31' },
          { id: '2', year: '2024-2025', startDate: '2024-08-01', endDate: '2025-05-31' }
        ];
        setBatches(mockBatches);
        setCurrentBatch(mockBatches[0]);
      }
    } catch (err) {
      console.error('BatchContext: Error in fetchBatches:', err);

      // Fall back to mock data
      console.log('BatchContext: Falling back to mock data due to error');
      const mockBatches = [
        { id: '1', year: '2023-2024', startDate: '2023-08-01', endDate: '2024-05-31' },
        { id: '2', year: '2024-2025', startDate: '2024-08-01', endDate: '2025-05-31' }
      ];
      setBatches(mockBatches);
      setCurrentBatch(mockBatches[0]);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedFetchBatches = useCallback(fetchBatches, [isAuthenticated, navigate]);

  useEffect(() => {
    console.log('BatchContext - Authentication status changed:', isAuthenticated);
    if (isAuthenticated) {
      console.log('BatchContext - User is authenticated, fetching batches');
      memoizedFetchBatches();
    } else {
      console.log('BatchContext - User is not authenticated, skipping fetch');
      setLoading(false);
    }
  }, [isAuthenticated, memoizedFetchBatches]); // Re-fetch when authentication status changes

  const handleSetCurrentBatch = async (batch: Batch) => {
    try {
      console.log('Setting current batch (user-specific):', batch);

      // Update local state immediately (no API call needed)
      setCurrentBatch(batch);

      // Save user's batch preference in localStorage
      saveUserCurrentBatch(batch);

      toast({
        title: 'Success',
        description: `Batch ${batch.year} is now set as your current working batch`,
      });
    } catch (err) {
      console.error('Error setting current batch:', err);
      let errorMessage = 'Failed to set current batch';

      if (err instanceof Error) {
        errorMessage += `: ${err.message}`;
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const hasBatches = batches.length > 0;

  return (
    <BatchContext.Provider value={{
      currentBatch,
      batches,
      setCurrentBatch: handleSetCurrentBatch,
      loading,
      error,
      refetchBatches: fetchBatches,
      hasBatches
    }}>
      {children}
    </BatchContext.Provider>
  );
}

export function useBatch() {
  const context = useContext(BatchContext);
  if (context === undefined) {
    // Return a default context instead of throwing an error to prevent blank screens
    console.warn('useBatch must be used within a BatchProvider. Using default values.');
    return {
      currentBatch: null,
      batches: [],
      setCurrentBatch: async () => {},
      loading: false,
      error: null,
      refetchBatches: async () => {},
      hasBatches: false
    };
  }
  return context;
}

// Backward compatibility - alias for existing code
export const AcademicYearProvider = BatchProvider;
